import type { PaginationMeta } from "@nasam/kernel";
import type {
  AlertCategory,
  AlertDetail,
  AlertNamedRef,
  AlertPriority,
  AlertRecipientOption,
  AlertsListQuery,
  AlertsRepository,
  AlertsSortBy,
  AlertStatus,
  AlertTarget,
  AlertTargetType,
  AlertTrigger,
  AlertTriggerKind,
  CreateAlertInput,
  UpdateAlertInput,
} from "../domain";
import { alertListItemFromDto } from "./alertMappers";

function category(
  code: string,
  nameEn: string,
  nameAr: string,
  triggerKind: AlertTriggerKind,
  sensorType: string | null,
): AlertCategory {
  return { id: `category-${code.toLowerCase()}`, code, nameEn, nameAr, triggerKind, sensorType };
}

/** The categories `GET /alerts/categories` returns, in display order. */
export const MOCK_ALERT_CATEGORIES: readonly AlertCategory[] = [
  category("SPEED", "Speed", "السرعة", "MAX_SPEED", null),
  category("DOWNTIME", "Down time", "وقت التوقف", "DOWNTIME", null),
  category("TEMPERATURE", "Temperature", "درجة الحرارة", "TEMPERATURE", "TEMPERATURE"),
  category("WEIGHT", "Weight", "الوزن", "RANGE", "WEIGHT"),
  category("PTO", "PTO", "PTO", "BINARY", "PTO"),
  category("SEAT_BELT", "Seat Belt", "حزام الأمان", "BINARY", "SEAT_BELT"),
  category("DOOR_STATE", "Door State", "حالة الباب", "BINARY", "DOOR_STATUS"),
  category("REFRIGERATOR", "Refrigerator", "حالة التبريد", "BINARY", "REFRIGERATOR_STATE"),
  category("HEADLIGHTS", "Headlights", "الأضواء الأمامية", "BINARY", "HEADLIGHTS"),
  category("SOS", "SOS", "استغاثة (SOS)", "BINARY", "SOS"),
  category("IBUTTON", "iButton", "آي بوتون", "BINARY", "IBUTTON"),
  category("OTHER", "Other", "أخرى", "THRESHOLD", null),
  category("DRIVER_FATIGUE", "Driver Fatigue", "إرهاق السائق", "DRIVER_BEHAVIOUR", null),
  category("PHONE_USE", "Phone Use", "استخدام الهاتف", "DRIVER_BEHAVIOUR", null),
  category("ENTERED_GEOFENCE", "Entered Geofence", "دخول منطقة جغرافية", "GEOFENCE_CROSSING", null),
  category("EXITED_GEOFENCE", "Exited Geofence", "خروج من منطقة جغرافية", "GEOFENCE_CROSSING", null),
];

export const MOCK_ALERT_RECIPIENTS: readonly AlertRecipientOption[] = [
  {
    id: "recipient-fleet-supervisor",
    username: "fleet.supervisor",
    email: "supervisor@acme.example.com",
    nameEn: "Fleet Supervisor",
    nameAr: "مشرف الأسطول",
  },
  {
    id: "recipient-operations-manager",
    username: "ops.manager",
    email: "ops@acme.example.com",
    nameEn: "Operations Manager",
    nameAr: "مدير العمليات",
  },
  {
    id: "recipient-safety-officer",
    username: "safety.officer",
    email: "safety@acme.example.com",
    nameEn: "Safety Officer",
    nameAr: "مسؤول السلامة",
  },
];

const ACME: AlertNamedRef = { id: "account-acme", nameEn: "Acme Logistics", nameAr: "أكمي للخدمات اللوجستية" };
const NAJD: AlertNamedRef = { id: "account-najd", nameEn: "Najd Transport", nameAr: "شركة نجد" };

const TRUCK_12 = { id: "asset-truck-12", code: "AST-000012", nameEn: "Truck 12", nameAr: "شاحنة 12" };
const REEFER_TYPE: AlertNamedRef = { id: "asset-type-reefer", nameEn: "Reefer truck", nameAr: "شاحنة مبردة" };
const RIYADH_DEPOT = { id: "geofence-riyadh-depot", name: "Riyadh Depot" };

function categoryOf(code: string): AlertCategory {
  return MOCK_ALERT_CATEGORIES.find((candidate) => candidate.code === code)!;
}

function seed(
  index: number,
  overrides: Partial<AlertDetail> & Pick<AlertDetail, "nameEn" | "nameAr" | "category" | "trigger">,
): AlertDetail {
  const account = index % 3 === 0 ? NAJD : ACME;
  const recipients = MOCK_ALERT_RECIPIENTS.slice(0, 1 + (index % 3));
  const targets: readonly AlertTarget[] = [
    {
      id: `target-${String(index)}`,
      assetType: null,
      asset: TRUCK_12,
      deviceType: null,
      device: null,
      geofence: null,
    },
  ];
  const createdAt = new Date(Date.UTC(2026, 0, 1 + index)).toISOString();
  return {
    id: `alert-${String(index)}`,
    description: null,
    priority: (["VERY_HIGH", "HIGH", "MEDIUM", "LOW"] as const)[index % 4]!,
    status: index % 5 === 4 ? "INACTIVE" : "ACTIVE",
    targetType: "ASSET",
    createdAt,
    updatedAt: createdAt,
    account,
    accountId: account.id,
    targets,
    targetIds: targets.map((target) => target.asset!.id),
    watchedAssets: [],
    assetIds: [],
    recipients,
    recipientIds: recipients.map((recipient) => recipient.id),
    channels: ["SYSTEM", "EMAIL"],
    alwaysActive: true,
    activeDays: [],
    repeatWeekly: false,
    startTime: null,
    endTime: null,
    startDate: null,
    endDate: null,
    ...overrides,
  };
}

function seedAlerts(): AlertDetail[] {
  return [
    seed(1, {
      nameEn: "Engine over-temperature",
      nameAr: "تجاوز حرارة المحرك",
      description: "Fires when the engine temperature reading rises above the safe limit.",
      category: categoryOf("OTHER"),
      trigger: { sensorType: "TEMPERATURE", operator: "GREATER_THAN", thresholdValue: 10, unit: "C" },
    }),
    seed(2, {
      nameEn: "Overspeed on highway",
      nameAr: "تجاوز السرعة على الطريق السريع",
      category: categoryOf("SPEED"),
      trigger: { maxSpeed: 120 },
      targetType: "ASSET_TYPE",
      targets: [
        {
          id: "target-2",
          assetType: REEFER_TYPE,
          asset: null,
          deviceType: null,
          device: null,
          geofence: null,
        },
      ],
      targetIds: [REEFER_TYPE.id],
    }),
    seed(3, {
      nameEn: "Cold-chain cargo out of range",
      nameAr: "خروج شحنة التبريد عن النطاق",
      category: categoryOf("TEMPERATURE"),
      trigger: { minTemperature: 2, maxTemperature: 8, minHumidity: 40, maxHumidity: 80 },
      alwaysActive: false,
      activeDays: ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY"],
      repeatWeekly: true,
      startTime: "08:00",
      endTime: "18:00",
    }),
    seed(4, {
      nameEn: "Overloaded trailer",
      nameAr: "حمولة زائدة على المقطورة",
      category: categoryOf("WEIGHT"),
      trigger: { minValue: 500, maxValue: 12000 },
    }),
    seed(5, {
      nameEn: "Seat belt unfastened",
      nameAr: "حزام الأمان غير مربوط",
      category: categoryOf("SEAT_BELT"),
      trigger: { alertValue: false },
    }),
    seed(6, {
      nameEn: "Long idle stop",
      nameAr: "توقف طويل",
      category: categoryOf("DOWNTIME"),
      trigger: { downtimeMinutes: 45 },
    }),
    seed(7, {
      nameEn: "Left the Riyadh depot",
      nameAr: "خروج من مستودع الرياض",
      category: categoryOf("EXITED_GEOFENCE"),
      trigger: {},
      targetType: "GEOFENCE",
      targets: [
        {
          id: "target-7",
          assetType: null,
          asset: null,
          deviceType: null,
          device: null,
          geofence: RIYADH_DEPOT,
        },
      ],
      targetIds: [RIYADH_DEPOT.id],
      watchedAssets: [TRUCK_12],
      assetIds: [TRUCK_12.id],
      alwaysActive: false,
      activeDays: ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY"],
      startTime: "08:00",
      endTime: "18:00",
      startDate: "2026-11-08",
      endDate: "2026-12-31",
    }),
  ];
}

function paginationMeta(total: number, page: number, limit: number): PaginationMeta {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return {
    totalCount: total,
    currentPage: page,
    totalPages,
    firstPageUrl: "",
    lastPageUrl: "",
    nextPageUrl: page < totalPages ? "" : null,
    previousPageUrl: page > 1 ? "" : null,
  };
}

const PRIORITY_RANK: Readonly<Record<AlertPriority, number>> = {
  VERY_HIGH: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};
const STATUS_RANK: Readonly<Record<AlertStatus, number>> = { ACTIVE: 0, INACTIVE: 1 };

function sortValue(alert: AlertDetail, sortBy: AlertsSortBy): string | number {
  switch (sortBy) {
    case "nameEn":
      return alert.nameEn;
    case "nameAr":
      return alert.nameAr;
    case "categoryNameEn":
      return alert.category.nameEn;
    case "categoryNameAr":
      return alert.category.nameAr;
    case "targetType":
      return alert.targetType;
    // Severity order, as the API sorts them.
    case "priority":
      return PRIORITY_RANK[alert.priority];
    case "status":
      return STATUS_RANK[alert.status];
    case "createdAt":
      return alert.createdAt;
  }
}

/** The API's rule: unique in either language, regardless of case. */
function sameName(stored: string, candidate: string): boolean {
  return stored.trim().toLowerCase() === candidate.trim().toLowerCase();
}

function triggerOf(input: CreateAlertInput | UpdateAlertInput): AlertTrigger {
  return { ...input.trigger };
}

function targetsOf(targetType: AlertTargetType, ids: readonly string[]): AlertTarget[] {
  return ids.map((id, index) => {
    const ref = { id, nameEn: id, nameAr: id };
    return {
      id: `target-${id}-${String(index)}`,
      assetType: targetType === "ASSET_TYPE" ? ref : null,
      asset: targetType === "ASSET" ? { ...ref, code: null } : null,
      deviceType: targetType === "DEVICE_TYPE" ? ref : null,
      device:
        targetType === "DEVICE" ? { id, imei: id, nameEn: null, nameAr: null } : null,
      geofence: targetType === "GEOFENCE" ? { id, name: id } : null,
    };
  });
}

/**
 * In-memory stand-in for the alerts endpoints, for tests and offline work.
 * Creates, updates and deletes really change what the list returns.
 */
export function createAlertsMockRepository(): AlertsRepository {
  const store = new Map<string, AlertDetail>(seedAlerts().map((alert) => [alert.id, alert]));
  let createdCount = 0;

  const nameTaken = (nameEn: string, nameAr: string, exceptId?: string) =>
    [...store.values()].some(
      (alert) =>
        alert.id !== exceptId && (sameName(alert.nameEn, nameEn) || sameName(alert.nameAr, nameAr)),
    );

  const fromInput = (
    id: string,
    input: CreateAlertInput | UpdateAlertInput,
    base: Pick<AlertDetail, "account" | "accountId" | "createdAt" | "status">,
  ): AlertDetail => {
    const recipients = MOCK_ALERT_RECIPIENTS.filter((recipient) =>
      input.recipientIds.includes(recipient.id),
    );
    return {
      id,
      nameEn: input.nameEn,
      nameAr: input.nameAr,
      description: input.description ?? null,
      priority: input.priority,
      status: "status" in input ? input.status : base.status,
      targetType: input.targetType,
      createdAt: base.createdAt,
      updatedAt: new Date().toISOString(),
      account: base.account,
      accountId: base.accountId,
      category: MOCK_ALERT_CATEGORIES.find((candidate) => candidate.id === input.categoryId)!,
      trigger: triggerOf(input),
      targets: targetsOf(input.targetType, input.targetIds),
      targetIds: [...input.targetIds],
      watchedAssets: (input.assetIds ?? []).map((assetId) => ({
        id: assetId,
        code: null,
        nameEn: assetId,
        nameAr: assetId,
      })),
      assetIds: [...(input.assetIds ?? [])],
      recipients,
      recipientIds: [...input.recipientIds],
      channels: [...input.channels],
      alwaysActive: input.alwaysActive,
      activeDays: input.alwaysActive ? [] : [...(input.activeDays ?? [])],
      repeatWeekly: !input.alwaysActive && input.repeatWeekly === true,
      startTime: input.alwaysActive ? null : (input.startTime ?? null),
      endTime: input.alwaysActive ? null : (input.endTime ?? null),
      startDate: input.alwaysActive ? null : (input.startDate ?? null),
      endDate: input.alwaysActive ? null : (input.endDate ?? null),
    };
  };

  return {
    list(query: AlertsListQuery = {}) {
      const page = query.page ?? 1;
      const limit = query.limit ?? 10;
      const sortBy = query.sortBy ?? "createdAt";
      // Newest first unless a direction was picked.
      const direction = (query.sort ?? (sortBy === "createdAt" ? "desc" : "asc")) === "asc" ? 1 : -1;
      const needle = query.search?.trim().toLowerCase();

      const rows = [...store.values()]
        .filter(
          (alert) =>
            (needle === undefined ||
              needle === "" ||
              [alert.nameEn, alert.nameAr, alert.category.nameEn, alert.category.nameAr]
                .some((value) => value.toLowerCase().includes(needle))) &&
            (query.categoryId === undefined || alert.category.id === query.categoryId) &&
            (query.targetType === undefined || alert.targetType === query.targetType) &&
            (query.priority === undefined || alert.priority === query.priority) &&
            (query.status === undefined || alert.status === query.status) &&
            (query.accountId === undefined || alert.accountId === query.accountId),
        )
        .sort((a, b) => {
          const left = sortValue(a, sortBy);
          const right = sortValue(b, sortBy);
          const compared =
            typeof left === "number" && typeof right === "number"
              ? left - right
              : String(left).localeCompare(String(right));
          return direction * compared || a.id.localeCompare(b.id);
        });

      const start = (page - 1) * limit;
      return Promise.resolve({
        data: rows.slice(start, start + limit).map(alertListItemFromDto),
        meta: paginationMeta(rows.length, page, limit),
      });
    },

    getById(id) {
      const alert = store.get(id);
      return alert === undefined
        ? Promise.reject(new Error("Alert not found"))
        : Promise.resolve(alert);
    },

    create(input) {
      if (nameTaken(input.nameEn, input.nameAr)) {
        return Promise.reject(new Error("Alert Name already exists."));
      }
      createdCount += 1;
      const id = `created-alert-${String(createdCount)}`;
      const account = input.accountId === NAJD.id ? NAJD : ACME;
      store.set(
        id,
        fromInput(id, input, {
          account,
          accountId: account.id,
          createdAt: new Date().toISOString(),
          status: "ACTIVE",
        }),
      );
      return Promise.resolve({ id });
    },

    update(id, input) {
      const existing = store.get(id);
      if (existing === undefined) return Promise.reject(new Error("Alert not found"));
      if (nameTaken(input.nameEn, input.nameAr, id)) {
        return Promise.reject(new Error("Alert Name already exists."));
      }
      store.set(id, fromInput(id, input, existing));
      return Promise.resolve({ id });
    },

    delete(id) {
      if (!store.delete(id)) return Promise.reject(new Error("Alert not found"));
      return Promise.resolve(undefined);
    },

    listCategories() {
      return Promise.resolve(MOCK_ALERT_CATEGORIES);
    },

    listRecipients() {
      return Promise.resolve(MOCK_ALERT_RECIPIENTS);
    },
  };
}
